import {
  getTownshipConfigurationDeniedRedirect,
  isTownshipConfigurationTownshipAllowed
} from './townshipAdminAccess';

type PermitAssignment = {
  permitRole?: string;
  departmentName?: string;
};

const REPORT_ALLOWED_PERMIT_IDS = [1, 2, 4, 5, 6, 7, 8, 10, 12];
const REPORT_MANAGER_ONLY_PERMIT_IDS = [9];

export const findPermitAssignment = (
  userDetails: Record<string, unknown> | null | undefined,
  permitId: number
): PermitAssignment | null => {
  const departments = userDetails?.TownshipPermits as
    | Array<{ DepartmentName?: string; Permits?: Array<{ PermitID: number; UserRoleName?: string }> }>
    | undefined;

  if (!Array.isArray(departments)) {
    return null;
  }

  for (const department of departments) {
    const permit = department.Permits?.find(
      (item) => Number(item.PermitID) === Number(permitId)
    );
    if (permit) {
      return {
        permitRole: permit.UserRoleName,
        departmentName: department.DepartmentName
      };
    }
  }

  return null;
};

/** Matches sidebar Reports link: Admin + allowed permit (or Manager on Construction). */
export const canAccessReports = (
  userDetails: Record<string, unknown> | null | undefined,
  permitId: number
): boolean => {
  if (!userDetails || userDetails.UserRole !== 'Admin') {
    return false;
  }

  const permitIdNum = Number(permitId);
  if (REPORT_ALLOWED_PERMIT_IDS.includes(permitIdNum)) {
    return true;
  }

  if (!REPORT_MANAGER_ONLY_PERMIT_IDS.includes(permitIdNum)) {
    return false;
  }

  return findPermitAssignment(userDetails, permitIdNum)?.permitRole === 'Manager';
};

/** Tree permit close-out: Manager on permit 4 only. */
export const canAccessPermitCloseOut = (
  userDetails: Record<string, unknown> | null | undefined,
  permitId: number
): boolean => {
  if (Number(permitId) !== 4) {
    return false;
  }

  return findPermitAssignment(userDetails, 4)?.permitRole === 'Manager';
};

/** Construction inspection schedule: permit 9, non-Manager permit role. */
export const canAccessInspectionSchedule = (
  userDetails: Record<string, unknown> | null | undefined,
  permitId: number
): boolean => {
  if (Number(permitId) !== 9) {
    return false;
  }

  const permitRole = findPermitAssignment(userDetails, 9)?.permitRole;
  return Boolean(permitRole && permitRole !== 'Manager');
};

/** Subscriptions page in sidebar: Admin only. */
export const canAccessSubscriptions = (
  userDetails: Record<string, unknown> | null | undefined
): boolean => userDetails?.UserRole === 'Admin';

/** Super Admin console routes. */
export const canAccessSuperAdminRoutes = (
  userDetails: Record<string, unknown> | null | undefined
): boolean => userDetails?.UserRole === 'Super Admin';

export const getPermitFeatureDeniedRedirect = getTownshipConfigurationDeniedRedirect;

export const isPermitFeatureTownshipAllowed = isTownshipConfigurationTownshipAllowed;
